import { useEffect, useMemo, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../Store/hooks';
import { getStudents } from '../../Store/StudentSlices/getStudentsSlice';
import { getCoachResources } from '../../Store/StudentSlices/getCoachResourcesSlice';
import type { PreviewKind, Resource, StudentOption } from './types';
import { formatResourceDate, mapFileTypeToResourceType } from './utils';
import AddResourcePanel from './AddResourcePanel';
import ResourcePreviewModal from './ResourcePreviewModal';
import ResourcesTable from './ResourcesTable';

const initialResources: Resource[] = [];

const ResourcesView = () => {
  const dispatch = useAppDispatch();
  const { coachInfo } = useAppSelector((state) => state.getCoach);
  const { resources: coachResources, loading: coachResourcesLoading } =
    useAppSelector((state) => state.getCoachResources);
  const { students: studentsPayload } = useAppSelector(
    (state) => state.getStudent
  );

  const [resources, setResources] = useState<Resource[]>(initialResources);
  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const [previewResource, setPreviewResource] = useState<Resource | null>(null);
  const [previewKind, setPreviewKind] = useState<PreviewKind>('none');

  const coachId = (coachInfo as any)?.id ?? '';

  const studentOptions = useMemo<StudentOption[]>(() => {
    const data =
      (studentsPayload as any)?.response ??
      (studentsPayload as any)?.data ??
      studentsPayload ??
      [];
    if (!Array.isArray(data)) {
      return [];
    }
    return data
      .map((student: any) => ({
        id: student?.user_id ?? student?.id ?? '',
        name: student?.name ?? 'Student',
      }))
      .filter((student: StudentOption) => Boolean(student.id));
  }, [studentsPayload]);

  const studentNameById = useMemo(() => {
    return new Map(studentOptions.map((student) => [student.id, student.name]));
  }, [studentOptions]);

  useEffect(() => {
    dispatch(getStudents());
  }, [dispatch]);

  useEffect(() => {
    if (coachId) {
      dispatch(getCoachResources(coachId));
    }
  }, [coachId, dispatch]);

  useEffect(() => {
    if (!Array.isArray(coachResources)) {
      return;
    }
    const mapped = coachResources.map((resource: any) => {
      const studentIds = Array.isArray(resource?.student_id)
        ? resource.student_id
        : resource?.student_id
        ? [resource.student_id]
        : [];
      const sharedWith =
        studentIds.length === 0
          ? ['All']
          : studentIds.map(
              (studentId: string) =>
                studentNameById.get(studentId) ?? 'Student'
            );
      return {
        id: resource?.id ?? `resource-${Date.now()}`,
        name: resource?.file_name ?? 'Resource',
        type: mapFileTypeToResourceType(resource?.file_type),
        sharedWith,
        addedOn: formatResourceDate(resource?.created_at),
        link: resource?.file_url,
      } as Resource;
    });
    setResources(mapped);
  }, [coachResources, studentNameById]);

  const handleResourceCreated = (resource: Resource) => {
    setResources((prev) => [resource, ...prev]);
  };

  const handleOpenPreview = (resource: Resource, kind: PreviewKind) => {
    setPreviewResource(resource);
    setPreviewKind(kind);
  };

  const handleClosePreview = () => {
    setPreviewResource(null);
    setPreviewKind('none');
  };

  return (
    <div className="flex flex-col gap-6">
      <div className={`flex flex-col gap-6 ${isPanelOpen ? 'xl:flex-row' : ''}`}>
        <ResourcesTable
          data={{ resources, isLoading: coachResourcesLoading }}
          actions={{
            onAddResource: () => setIsPanelOpen(true),
            onOpenPreview: handleOpenPreview,
          }}
        />
        <AddResourcePanel
          isOpen={isPanelOpen}
          coachId={coachId}
          students={studentOptions}
          onClose={() => setIsPanelOpen(false)}
          onResourceCreated={handleResourceCreated}
        />
      </div>
      <ResourcePreviewModal
        data={{ resource: previewResource, kind: previewKind }}
        actions={{ onClose: handleClosePreview }}
      />
    </div>
  );
};

export default ResourcesView;
